import React from 'react'
import {
  AutoApprovedIcon,
  Col,
  ResetIcon,
  Row,
  UserJoin,
  UserLeave,
  CreatedIcon,
  CheckMarkWithAddIcon,
  UserProfile,
} from '@modifi/ui'
import {connect} from '@modifi/redux'
import {TaskCategory, TaskStatus} from '@modifi/enums'
import {selectAuthenticationUserName} from '@modifi/plugin-authentication'
import {useTranslation} from '@modifi/plugin-i18n'
import {aShowDialog} from '@modifi/plugin-dialogs'

import ActionItem from './ActionItem'
import {aSubmit, selectUpdatePending} from '../../store'
import {TaskCommonActions, Task, TaskUpdate} from '../../constants'
import {INTERNAL_NOTE_DIALOG, RESOLVE_TASK_DIALOG} from '../../dialogs'

import {aAssignTaskToAccountOwner} from '../../stores/assign-task-to-account-owner'
import {useAbility} from '../../../../constants/permissions'

type ActionsDropdownContentProps = {
  task: Task
  loggedInUser?: string
  isUpdatePending?: boolean
  updateTask: (taskId: string, changes: TaskUpdate, actionMessage?: string) => void
  showResolveTaskDialog: (taskId: string, internalNote?: string, category?: TaskCategory) => void
  closeDropdown?: () => void
  showAddNoteDialog: (
    taskId: string,
    internalNote?: string,
    internalNoteModifiedBy?: string,
    isEditAction?: boolean
  ) => void
  assignTaskToAccountOwner: (
    taskId: string,
    externalCompanyId: string,
    actionMessage?: string
  ) => void
}

const ActionsDropdownContent: React.FC<ActionsDropdownContentProps> = ({
  task,
  loggedInUser = '',
  isUpdatePending = false,
  updateTask,
  showResolveTaskDialog,
  closeDropdown = undefined,
  showAddNoteDialog,
  assignTaskToAccountOwner,
}) => {
  const {t} = useTranslation('kartavya')
  const ability = useAbility()
  const canPerformAction = ability.can('create', 'taskActions')

  const handleActionClick = (taskActionType: string) => {
    let change: TaskUpdate | null = null
    switch (taskActionType) {
      case TaskCommonActions.REOPEN_TASK:
        change = {status: TaskStatus.TODO}
        break
      case TaskCommonActions.LEAVE_TASK:
        change = {assignee: 'NONE'}
        break
      case TaskCommonActions.ASSIGN_TASK:
        change = {assignee: loggedInUser}
        break
      case TaskCommonActions.SET_TO_TODO:
        change = {status: TaskStatus.TODO}
        break
      default:
    }

    if (taskActionType === TaskCommonActions.ASSIGN_TO_ACCOUNT_OWNER) {
      assignTaskToAccountOwner(
        task.externalId,
        task.externalCompanyId,
        t(`tasks.action.${taskActionType}.success-message`)
      )
    } else if (change) {
      !isUpdatePending &&
        updateTask(task.externalId, change, t(`tasks.action.${taskActionType}.success-message`))
    }

    closeDropdown && closeDropdown()
  }

  return (
    <Row>
      {canPerformAction && (
        <Col>
          {task.status === TaskStatus.RESOLVED ? (
            <ActionItem
              icon={ResetIcon}
              label={t(`tasks.action.${TaskCommonActions.REOPEN_TASK}`)}
              iconSize={18}
              onClick={() => handleActionClick(TaskCommonActions.REOPEN_TASK)}
              ident="reopen-task"
            />
          ) : (
            <ActionItem
              icon={AutoApprovedIcon}
              label={t(`tasks.action.${TaskCommonActions.RESOLVE_TASK}`)}
              onClick={() =>
                showResolveTaskDialog(task.externalId, task.internalNote, task.category)
              }
              ident="resolve-task"
            />
          )}
        </Col>
      )}
      <Col>
        {task.assignee === loggedInUser ? (
          <ActionItem
            icon={UserLeave}
            label={t(`tasks.action.${TaskCommonActions.LEAVE_TASK}`)}
            iconSize={24}
            onClick={() => handleActionClick(TaskCommonActions.LEAVE_TASK)}
            ident="leave-task"
          />
        ) : (
          <ActionItem
            icon={UserJoin}
            label={t(`tasks.action.${TaskCommonActions.ASSIGN_TASK}`)}
            iconSize={24}
            onClick={() => handleActionClick(TaskCommonActions.ASSIGN_TASK)}
            ident="assign-task"
          />
        )}
      </Col>
      {task.status !== TaskStatus.RESOLVED && (
        <Col>
          <ActionItem
            icon={CreatedIcon}
            label={t(`tasks.action.${task.internalNote ? 'edit-note' : 'add-note'}.label`)}
            iconSize={24}
            onClick={() =>
              showAddNoteDialog(
                task.externalId,
                task.internalNote,
                task.internalNoteModifiedBy,
                true
              )
            }
            ident={task?.internalNote ? 'edit-note' : 'add-note'}
          />
        </Col>
      )}
      {task.status === TaskStatus.IN_REVIEW && canPerformAction && (
        <Col>
          <ActionItem
            icon={CheckMarkWithAddIcon}
            label={t('tasks.action.set-to-todo.label')}
            iconSize={24}
            onClick={() => handleActionClick(TaskCommonActions.SET_TO_TODO)}
            ident="set-todo"
          />
        </Col>
      )}
      {canPerformAction && (
        <Col>
          <ActionItem
            icon={UserProfile}
            label={t('tasks.action.assign-to-account-owner.label')}
            iconSize={24}
            onClick={() => handleActionClick(TaskCommonActions.ASSIGN_TO_ACCOUNT_OWNER)}
            ident="assign-to-account-owner"
          />
        </Col>
      )}
    </Row>
  )
}

const mapStateToProps = (
  state: Record<string, any>
): Pick<ActionsDropdownContentProps, 'loggedInUser' | 'isUpdatePending'> => ({
  loggedInUser: selectAuthenticationUserName(state),
  isUpdatePending: selectUpdatePending(state),
})

const mapDispatchToProps = {
  updateTask: aSubmit,
  showResolveTaskDialog: (
    taskId: string,
    internalNote?: string,
    category?: TaskCategory
  ): ReturnType<typeof aShowDialog> =>
    aShowDialog(RESOLVE_TASK_DIALOG, {taskId, internalNote, category}),
  showAddNoteDialog: (
    taskId: string,
    internalNote?: string,
    internalNoteModifiedBy?: string,
    isEditAction?: boolean
  ): ReturnType<typeof aShowDialog> =>
    aShowDialog(INTERNAL_NOTE_DIALOG, {taskId, internalNote, internalNoteModifiedBy, isEditAction}),
  assignTaskToAccountOwner: aAssignTaskToAccountOwner,
}

export default connect(mapStateToProps, mapDispatchToProps)(ActionsDropdownContent)
